import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Edit3, Loader2, IdCard } from "lucide-react";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/StatusBadge";
import { LoadingBlock, EmptyState } from "@/components/Pagination";
import { api, formatApiError } from "@/lib/api";
import { ADMIN } from "@/constants/adminTestIds";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";

const EMPTY = { designation_name: "", designation_code: "", description: "", status: "active" };

export default function DesignationsPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/org/designations");
      setItems(data.data.designations);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setForm(EMPTY); setDlg({ create: true }); };
  const openEdit = (d) => {
    setForm({ designation_name: d.designation_name, designation_code: d.designation_code, description: d.description || "", status: d.status });
    setDlg(d);
  };

  const submit = async () => {
    setSaving(true);
    try {
      if (dlg.create) {
        await api.post("/org/designations", form);
        toast.success("Designation created.");
      } else {
        const body = { designation_name: form.designation_name, description: form.description, status: form.status };
        await api.put(`/org/designations/${dlg.id}`, body);
        toast.success("Designation updated.");
      }
      setDlg(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const toggle = async (d) => {
    try {
      await api.patch(`/org/designations/${d.id}/status`, { status: d.status === "active" ? "inactive" : "active" });
      load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <DashboardLayout title="Designations" breadcrumbs={["My Institute", "Designations"]}>
      <div className="space-y-4" data-testid={ADMIN.designationsRoot}>
        <div className="rounded-2xl border border-slate-200 bg-white p-4">
          <div className="flex items-center justify-between gap-3">
            <div className="text-sm text-slate-600">Create the job roles your staff will hold — Staff Nurse, Consultant, Junior Resident etc.</div>
            <button onClick={openCreate} className="inline-flex items-center gap-1.5 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c]" data-testid={ADMIN.designationsAddBtn}>
              <Plus className="h-4 w-4" /> Add designation
            </button>
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white">
          {loading ? <LoadingBlock label="Loading designations…" /> : items.length === 0 ? (
            <EmptyState title="No designations yet" description="Create designations that staff can be assigned to." icon={IdCard} />
          ) : (
            <Table>
              <TableHeader><TableRow>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Name</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Code</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Description</TableHead>
                <TableHead className="text-xs uppercase tracking-wider text-slate-500">Status</TableHead>
                <TableHead className="text-right text-xs uppercase tracking-wider text-slate-500">Actions</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {items.map((d) => (
                  <TableRow key={d.id}>
                    <TableCell className="font-semibold text-slate-900">{d.designation_name}</TableCell>
                    <TableCell className="font-mono text-xs text-slate-600">{d.designation_code}</TableCell>
                    <TableCell className="text-sm text-slate-600">{d.description || "—"}</TableCell>
                    <TableCell><StatusBadge status={d.status} /></TableCell>
                    <TableCell className="text-right">
                      <div className="inline-flex items-center gap-1">
                        <button onClick={() => openEdit(d)} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-slate-500 hover:bg-slate-100 hover:text-[#101876]"><Edit3 className="h-3.5 w-3.5" /></button>
                        <button onClick={() => toggle(d)} className="inline-flex h-7 items-center justify-center rounded-md px-2 text-xs font-semibold text-slate-500 hover:bg-slate-100 hover:text-[#101876]">
                          {d.status === "active" ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </div>

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent className="sm:max-w-md" data-testid={ADMIN.designationForm}>
          <DialogHeader><DialogTitle>{dlg?.create ? "Add designation" : `Edit ${dlg?.designation_name}`}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label>Name *</Label><Input value={form.designation_name} onChange={(e) => setForm({ ...form, designation_name: e.target.value })} data-testid="designation-name" /></div>
            {dlg?.create && (
              <div className="space-y-1"><Label>Code *</Label><Input value={form.designation_code} onChange={(e) => setForm({ ...form, designation_code: e.target.value.toUpperCase() })} data-testid="designation-code" placeholder="e.g. STAFF_NURSE" /></div>
            )}
            <div className="space-y-1"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <button onClick={() => setDlg(null)} className="rounded-md border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-50">Cancel</button>
            <button onClick={submit} disabled={saving} className="inline-flex items-center gap-2 rounded-md bg-[#101876] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0c135c] disabled:opacity-70" data-testid={ADMIN.designationFormSubmit}>
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {dlg?.create ? "Create" : "Save"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
